import { useEffect, useState } from "react";
import type { KeyedMutator } from "swr";

import type {
  Automation,
  AutomationInput,
} from "../../server/lads-api/clients/lcc-openapi";
import { receiveLADSAutomation } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { useActiveRoom } from "../hooks/use-rooms";
import type {
  Device,
  DeviceWithAutomations,
  SupportedAutomations,
  TimeObject,
  ViewAutomation,
  WeekDays,
} from "../view-model";
import { TimeControl } from "./time-control";
import type {
  OnSelectDayProps,
  OnToggleAutomationProps,
} from "./time-control.tsx";
import type { OnSubmitProps } from "./time-control-modal";
import { TimeControlModal } from "./time-control-modal";

function weekDayToDayNumber(day: WeekDays): number {
  const dayMap: Record<WeekDays, number> = {
    day1: 1,
    day2: 2,
    day3: 3,
    day4: 4,
    day5: 5,
    day6: 6,
    day7: 7,
  };
  return dayMap[day] || 1;
}

function createLADSAutomationInput(
  deviceId: string,
  commandType: SupportedAutomations,
  startTime: string,
  endTime: string,
  days: WeekDays[],
  roomId: string,
  name: string
): AutomationInput {
  const functionPath =
    commandType === "light"
      ? `DeviceSet/${deviceId}/FunctionalUnitSet/Fumehood/FunctionSet/Light`
      : `DeviceSet/${deviceId}/FunctionalUnitSet/Fumehood/FunctionSet/Operation`;

  const targetValueStart = commandType === "light" ? "On" : "normal";
  const targetValueEnd = commandType === "light" ? "Off" : "low";

  const dayNumbers = days.map((day) => weekDayToDayNumber(day));

  return {
    roomId,
    deviceId,
    name,
    enabled: true,
    schedule: {
      start: startTime,
      end: endTime,
      period: dayNumbers,
    },
    targetFunction: {
      path: functionPath,
      targetValueStart,
      targetValueEnd,
    },
  };
}

type TimeControlPageProps = {
  devices: Device[];
  automations: ViewAutomation[];
  createAutomation: (
    automation: AutomationInput,
    accessToken?: string
  ) => Promise<Automation>;
  deleteAutomation: (
    automationId: string,
    accessToken?: string
  ) => Promise<void>;
  updateAutomation: (
    automationId: string,
    automation: AutomationInput,
    accessToken?: string
  ) => Promise<Automation>;
  mutateAutomations: KeyedMutator<ViewAutomation[]>;
};

export const TimeControlPagePartial = ({
  automations,
  createAutomation,
  deleteAutomation,
  devices,
  mutateAutomations,
  updateAutomation,
}: TimeControlPageProps) => {
  const { activeRoom } = useActiveRoom();
  const devicesWithAutomations: DeviceWithAutomations[] = devices.map(
    (device) => ({
      ...device,
      automations: {
        light:
          automations.find(
            (automation) => automation.light?.deviceId === device.id
          )?.light || {},
        operation:
          automations.find(
            (automation) => automation.operation?.deviceId === device.id
          )?.operation || {},
      },
    })
  );

  const token = useAccessToken();
  const [selectedCommand, setSelectedCommand] =
    useState<SupportedAutomations>("light");
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithAutomations>(
    devicesWithAutomations[0]
  );
  const [selectedDay, setSelectedDay] = useState<WeekDays>("day1");
  const [selectedDays, setSelectedDays] = useState<WeekDays[]>([]);
  const [isUsedForAllDevices, setIsUsedForAllDevices] = useState(false);
  const [selectedTime, setSelectedTime] = useState<TimeObject>({});
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedIsDisabled, setSelectedIsDisabled] = useState(
    Object.keys(selectedTime).length === 0
  );

  useEffect(() => {
    if (selectedDevice) {
      const selectedAutomations = selectedDevice.automations;
      const command = selectedAutomations[selectedCommand];
      if (command) {
        const time = command[selectedDay] || {};
        setSelectedTime(time);
      }
    }
  }, [selectedCommand, selectedDay, selectedDevice, setSelectedCommand]);

  const onSelectDay = ({
    selectedAutomation,
    selectedDay: day,
    selectedDevice: device,
  }: OnSelectDayProps) => {
    setSelectedDay(day);
    setSelectedDays([day]);
    setSelectedDevice(device);
    setSelectedCommand(selectedAutomation);
    if (
      device.automations[selectedAutomation] &&
      Object.keys(device.automations[selectedAutomation] || {}).length > 0
    ) {
      const automation = device.automations[selectedAutomation];
      if (automation && automation[day]) {
        setSelectedIsDisabled(false);
      } else {
        setSelectedIsDisabled(true);
      }
    } else {
      setSelectedIsDisabled(false);
    }
    setIsOpenModal(true);
  };

  const onSubmit = async (props: OnSubmitProps) => {
    const affectedDevices = props.isUsedForAllDevices
      ? devicesWithAutomations
      : [props.device];

    const roomId = activeRoom?.id || "";

    let currentAutomations = [...automations];
    const timeControlPromises = affectedDevices.map(async (device) => {
      const existingAutomations = device.automations[props.commandType];
      let newAutomations = {
        ...existingAutomations,
        deviceId: device.id,
        enabled: existingAutomations?.enabled || true,
      };

      // Update the days in newAutomations
      for (const day of props.selectedDays) {
        if (selectedIsDisabled) {
          delete newAutomations[day];
        } else {
          newAutomations = {
            ...newAutomations,
            [day]: {
              startTime: props.startTime,
              endTime: props.endTime,
            },
          };
        }
      }

      // Delete existing automation if it exists
      if (existingAutomations && existingAutomations.id) {
        currentAutomations = currentAutomations.filter((updatedAutomation) => {
          if (updatedAutomation[props.commandType]) {
            return (
              updatedAutomation[props.commandType]?.id !==
              existingAutomations.id
            );
          }
          return true;
        });

        await deleteAutomation(
          existingAutomations.id as string,
          token ?? undefined
        );
      }

      // Create new automation if there are enabled days
      const enabledDays = props.selectedDays.filter(() => !selectedIsDisabled);

      if (enabledDays.length > 0 && props.startTime && props.endTime) {
        const automationInput = createLADSAutomationInput(
          device.id,
          props.commandType,
          props.startTime,
          props.endTime,
          enabledDays,
          roomId,
          `${device.id}_${props.commandType}`
        );

        await mutateAutomations(
          async () => {
            const response = await createAutomation(
              automationInput,
              token ?? undefined
            );
            currentAutomations = [
              {
                [props.commandType]: {
                  ...newAutomations,
                  id: response.id,
                  name: response.name,
                },
              },
              ...currentAutomations,
            ];
            return currentAutomations;
          },
          {
            revalidate: false,
          }
        );
      }
    });
    await Promise.all(timeControlPromises);
    await mutateAutomations(currentAutomations, {
      revalidate: false,
    });

    onHandleCloseModal();
  };

  const onChangeDisabled = () => {
    setSelectedIsDisabled(!selectedIsDisabled);

    if (selectedIsDisabled) {
      if (selectedDevice) {
        const selectedAutomations = selectedDevice.automations;
        const command = selectedAutomations[selectedCommand];
        const existingTime = command?.[selectedDay];

        setSelectedTime(
          existingTime || { startTime: "08:00", endTime: "18:00" }
        );
      }
    } else {
      setSelectedTime({});
    }
  };

  const onToggleAutomation = async (props: OnToggleAutomationProps) => {
    const automation =
      props.selectedDevice.automations[props.selectedAutomation];

    if (automation && automation.id && automation.name) {
      const updatedAutomations: ViewAutomation[] = automations.map(
        (updatedAutomation) => {
          if (
            updatedAutomation[props.selectedAutomation] &&
            updatedAutomation[props.selectedAutomation]?.id === automation.id
          ) {
            return {
              [props.selectedAutomation]: {
                ...automation,
                enabled: !automation.enabled,
              },
            } as ViewAutomation;
          }
          return updatedAutomation;
        }
      );
      mutateAutomations(updatedAutomations, {
        revalidate: false,
      });

      // Fetch the existing automation to get all required fields
      const existingAutomation = await receiveLADSAutomation(
        automation.id as string,
        token ?? undefined
      );

      // Convert Automation to AutomationInput and update only enabled
      const automationInput: AutomationInput = {
        roomId: existingAutomation.roomId,
        deviceId: existingAutomation.deviceId,
        name: existingAutomation.name,
        description: existingAutomation.description,
        enabled: !automation.enabled,
        schedule: existingAutomation.schedule,
        targetFunction: existingAutomation.targetFunction,
      };

      await updateAutomation(
        automation.id as string,
        automationInput,
        token ?? undefined
      );
    } else {
      setSelectedDevice(props.selectedDevice);
      setSelectedIsDisabled(false);
      setSelectedDays(["day1"]);
      setSelectedCommand(props.selectedAutomation);
      setIsOpenModal(true);
    }
  };

  const onHandleCloseModal = () => {
    setSelectedDays([]);
    setIsUsedForAllDevices(false);
    setIsOpenModal(false);
  };

  const onSelectDays = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      event.target.checked &&
      !selectedDays.includes(event.target.value as WeekDays)
    ) {
      setSelectedDays([...selectedDays, event.target.value as WeekDays]);
    } else {
      setSelectedDays(
        selectedDays.filter((day) => day !== (event.target.value as WeekDays))
      );
    }
  };

  return (
    <>
      <TimeControl
        devices={devicesWithAutomations}
        onSelectDay={onSelectDay}
        onToggleAutomation={onToggleAutomation}
      />
      <TimeControlModal
        commandType={selectedCommand}
        device={selectedDevice}
        disabled={selectedIsDisabled}
        initialEndTime={selectedTime?.endTime}
        initialStartTime={selectedTime?.startTime}
        isOpenModal={isOpenModal}
        isUsedForAllDevices={isUsedForAllDevices}
        onChangeDisabled={onChangeDisabled}
        onHandleCloseModal={onHandleCloseModal}
        onSelectDays={onSelectDays}
        onSubmit={onSubmit}
        selectedDays={selectedDays}
        setIsOpenModal={setIsOpenModal}
        setIsUsedForAllDevices={setIsUsedForAllDevices}
      />
    </>
  );
};
